import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Plug, RefreshCw, Save, CheckCircle2, XCircle } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClearviewDialog({ open, onOpenChange }) {
  const [state, setState] = useState(null);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/integrations/clearview/status`);
      setState(res.data);
      setMapping(Object.fromEntries(res.data.stores.map(s => [s.id, s.clearview_store_id || ''])));
    } catch { toast.error('Could not load Clearview status'); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { const r = await fn(); if (okMsg) toast.success(typeof okMsg === 'function' ? okMsg(r.data) : okMsg); await load(); return r; }
    catch (e) { toast.error(e.response?.data?.detail || 'Request failed'); }
    finally { setBusy(''); }
  };

  const saveMapping = () => run('save', () => axios.put(`${API_URL}/integrations/clearview/stores`, { mappings: mapping }), 'Store mapping saved');
  const testCreds = () => run('test', async () => { const r = await axios.post(`${API_URL}/integrations/clearview/test`); r.data.ok ? toast.success(r.data.message) : toast.error(`${r.data.reason}: ${r.data.detail || (r.data.missing_env || []).join(', ')}`); return r; });
  const syncNow = () => run('sync', () => axios.post(`${API_URL}/integrations/clearview/sync-now`, {}), d => `Sync ${d.status}${d.results ? ` · ${d.results.length} store(s)` : ''}${d.errors?.length ? ` · ${d.errors.length} error(s)` : ''}`);

  const last = state?.last_run;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="clearview-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Plug className="w-5 h-5 text-orange-500" /> Clearview nightly sync</DialogTitle></DialogHeader>
        {!state ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div> : (
          <div className="space-y-4 text-sm">
            <div className={`rounded-lg p-3 flex items-start gap-2 ${state.configured ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`} data-testid="clearview-config-state">
              {state.configured ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <XCircle className="w-4 h-4 mt-0.5" />}
              <div>
                <p className="font-semibold">{state.configured ? 'Credentials present — runs nightly at 2am Atlantic' : 'Waiting for Clearview API credentials'}</p>
                {!state.configured && <p className="text-xs mt-1">Add to the backend environment: {state.missing_env.join(', ')} (from Clearview Partner Connect onboarding). Optional: CLEARVIEW_SCOPE, CLEARVIEW_ORG_ID, CLEARVIEW_SUMMARY_PATH.</p>}
              </div>
            </div>

            <div>
              <p className="font-semibold text-slate-800 mb-1">Store mapping <span className="font-normal text-slate-500">— Clearview store ID for each location ({state.mapped_stores}/{state.stores.length} mapped)</span></p>
              <div className="grid sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1">
                {state.stores.map(s => (
                  <label key={s.id} className="flex items-center gap-2">
                    <span className="w-36 truncate text-slate-600">{s.name}</span>
                    <Input value={mapping[s.id] || ''} onChange={e => setMapping(m => ({ ...m, [s.id]: e.target.value }))} placeholder="e.g. 102" className="h-8 text-xs" data-testid={`cv-map-${s.id}`} />
                  </label>
                ))}
              </div>
            </div>

            {last && (
              <p className="text-xs text-slate-500" data-testid="clearview-last-run">
                Last run: <b>{last.status}</b> · week {last.week} · {new Date(last.started_at).toLocaleString()}{last.errors?.length ? ` · ${last.errors.length} error(s): ${last.errors[0].store} — ${last.errors[0].error}` : ''}
              </p>
            )}

            <div className="flex flex-wrap justify-end gap-2 pt-1">
              <Button variant="outline" onClick={saveMapping} disabled={!!busy} className="rounded-sm" data-testid="cv-save-mapping-btn">{busy === 'save' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save mapping</Button>
              <Button variant="outline" onClick={testCreds} disabled={!!busy} className="rounded-sm" data-testid="cv-test-btn">{busy === 'test' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plug className="w-4 h-4 mr-2" />}Test credentials</Button>
              <Button onClick={syncNow} disabled={!!busy || !state.configured} className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm" data-testid="cv-sync-now-btn">{busy === 'sync' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}Sync last week now</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
